import { ApiError } from "../../lib/api.js";

/**
 * The error MESSAGE plus whatever the agent actually said.
 *
 * The proxy collapses any upstream 4xx to "Sales agent returned 422" and nests
 * the real reason under `details.upstream_body`. Showing only the first half is
 * how a 422 reached the operator on 2026-08-07 as a bare status code with no
 * clue that the request body had been double-encoded. A surface built to stop
 * hiding what happened should not hide this either.
 *
 * The agent's `detail` arrives in one of two shapes and both are rendered as
 * prose, never as raw JSON:
 *   - its own typed `{detail: {error, code}}` — the `error` string IS the
 *     sentence.
 *   - FastAPI's own request-validation shape, `{detail: [{loc, msg, type}]}`
 *     — one entry per invalid field. Showing the array unread (the bug fixed
 *     2026-08-27: a 422 on refold rendered as `{"detail":[{"type":…`,
 *     truncated mid-JSON) is exactly the "raw status code" failure this
 *     helper exists to prevent, just one level deeper.
 *
 * This is the ONE unwrapper for every sales surface that turns a failed
 * mutation into operator-facing copy — `SteeringNoteInput`'s send/preview,
 * `ActiveDirectives`'s retry, `StrategyPage`'s refresh, and
 * `CampaignEditor`'s save — so a future proxy change only needs updating
 * here.
 */
export function describeAgentError(
  err: unknown,
  fallback = "Something went wrong.",
): string {
  if (!(err instanceof Error)) return fallback;
  const details = err instanceof ApiError ? (err.details as { upstream_body?: unknown }) : null;
  const upstream = details?.upstream_body;
  let detail = "";
  if (upstream && typeof upstream === "object") {
    detail = describeDetail((upstream as { detail?: unknown }).detail);
    // Neither known shape matched — fall back to a bounded raw dump rather
    // than nothing at all, but this is the exception, not the common case.
    if (!detail) detail = JSON.stringify(upstream).slice(0, 200);
  } else if (typeof upstream === "string") {
    detail = upstream.slice(0, 200);
  }
  return detail ? `${err.message} — ${detail}` : err.message;
}

/** Dig the human sentence out of a FastAPI `detail`, in either shape the
 *  agent sends it. Returns "" when neither shape yields anything usable, so
 *  the caller can fall back in its own way. */
function describeDetail(detail: unknown): string {
  if (Array.isArray(detail)) {
    return detail.map(describeValidationItem).filter(Boolean).join("; ");
  }
  if (detail && typeof detail === "object") {
    const error = (detail as { error?: unknown }).error;
    return typeof error === "string" && error.trim() ? error : "";
  }
  if (typeof detail === "string" && detail.trim()) return detail;
  return "";
}

/** One FastAPI validation-error item → "field: message" (or just the
 *  message when `loc` is empty/unusable). The "body" root segment is a
 *  Pydantic implementation detail the operator has no use for. */
function describeValidationItem(item: unknown): string {
  const loc = Array.isArray((item as { loc?: unknown[] })?.loc)
    ? (item as { loc: unknown[] }).loc.filter((p) => p !== "body").join(".")
    : "";
  const msg = String((item as { msg?: unknown })?.msg ?? "").trim();
  if (!msg) return "";
  return loc ? `${loc}: ${msg}` : msg;
}
